<template>
	<!-- Small-screen More menu -->
	<Dropdown :position="'bottom-end'" class="sm:hidden">
		<template #trigger>
			<button
				ref="moreTriggerRef"
				type="button"
				class="p-1 shrink-0 inline-flex items-center justify-center"
				:title="$t('More')"
			>
				<EllipsisVerticalIcon class="w-5 h-5" />
			</button>
		</template>
		<template #content>
			<div class="bg-white rounded-lg shadow-lg p-2 min-w-[10rem] text-sm sm:text-base">
				<button
					type="button"
					class="w-full text-left px-3 py-2 hover:bg-gray-100 rounded"
					:class="{ 'bg-indigo-100': isLinkActive }"
					@click="openLinkDialog"
				>
					<span class="inline-flex items-center gap-2">
						<LinkIcon class="w-4 h-4" />
						<span>{{ $t("Insert link") }}</span>
					</span>
				</button>
				<button
					type="button"
					class="w-full text-left px-3 py-2 hover:bg-gray-100 rounded"
					:class="{ 'bg-indigo-100': isBulletActive }"
					@click="toggleBulletList"
				>
					<span class="inline-flex items-center gap-2">
						<ListBulletIcon class="w-4 h-4" />
						<span>{{ $t("Bullet list") }}</span>
					</span>
				</button>
				<button
					type="button"
					class="w-full text-left px-3 py-2 hover:bg-gray-100 rounded"
					:class="{ 'bg-indigo-100': isOrderedActive }"
					@click="toggleOrderedList"
				>
					<span class="inline-flex items-center gap-2">
						<NumberedListIcon class="w-4 h-4" />
						<span>{{ $t("Numbered list") }}</span>
					</span>
				</button>
			</div>
		</template>
	</Dropdown>

	<!-- Link Modal for small screens (centered) -->
	<div v-if="showLinkModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
		<div
			class="w-[88vw] max-w-[20rem] sm:w-auto sm:min-w-[20rem] bg-white rounded-lg shadow-2xl p-4 my-3 max-h-[80vh] overflow-y-auto"
		>
			<div class="flex items-center justify-between mb-2">
				<h3 class="text-sm font-semibold">
					{{ linkMode === "edit" ? $t("Edit link") : $t("Insert link") }}
				</h3>
				<button
					v-if="linkMode === 'edit'"
					type="button"
					class="btn btn-xs btn-primary"
					:title="$t('Remove')"
					@click="unsetLink"
				>
					<LinkSlashIcon class="w-4 h-4" />
				</button>
			</div>
			<div class="flex flex-col gap-2">
				<label class="text-xs">
					<span class="block mb-1">{{ $t("Title") }} ({{ $t("Optional").toLowerCase() }})</span>
					<input
						ref="linkTitleInput"
						v-model="linkTitle"
						type="text"
						class="input input-bordered w-full input-sm select-none"
						:placeholder="$t('Text')"
					/>
				</label>
				<label class="text-xs">
					<span class="block mb-1">{{ $t("Link") }}</span>
					<div class="flex items-center gap-2">
						<input
							ref="linkUrlInput"
							v-model="linkUrl"
							type="url"
							class="input input-bordered w-full input-sm flex-1 select-none"
							placeholder="https://example.com"
							@keydown.enter.prevent.stop="submitLink"
						/>
						<button
							v-if="linkMode === 'edit' && linkInfoHref"
							type="button"
							class="btn btn-xs"
							@click="$emit('openExternally')"
						>
							<ArrowTopRightOnSquareIcon class="w-4 h-4" />
						</button>
					</div>
				</label>
			</div>
			<div class="mt-3 flex justify-end items-center gap-2">
				<button type="button" class="btn btn-xs" @click="cancelLink">{{ $t("Cancel") }}</button>
				<button type="button" class="btn btn-xs btn-primary" @click="submitLink">
					{{ $t("Save") }}
				</button>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import Dropdown from "@/components/ui/Dropdown.vue";
import { getMarkRange } from "@tiptap/core";
import { Editor } from "@tiptap/vue-3";
import { moveCaretOutOfLink, stripProtocol, normalizeUrl } from "@/components/ui/texteditor/utils/linkUtils";
import {
	EllipsisVerticalIcon,
	LinkIcon,
	LinkSlashIcon,
	ListBulletIcon,
	NumberedListIcon,
	ArrowTopRightOnSquareIcon,
} from "@heroicons/vue/24/outline";

export default defineComponent({
	name: "MoreActionsMenu",
	components: {
		Dropdown,
		EllipsisVerticalIcon,
		LinkIcon,
		LinkSlashIcon,
		ListBulletIcon,
		NumberedListIcon,
		ArrowTopRightOnSquareIcon,
	},
	emits: ["openExternally"],
	props: {
		editor: { type: Object as PropType<Editor | null>, required: true },
		// reactive flags from parent so this menu reflects active state while open
		isBulletActive: { type: Boolean, default: false },
		isOrderedActive: { type: Boolean, default: false },
	},
	data() {
		return {
			showLinkModal: false as boolean,
			linkTitle: "" as string,
			linkUrl: "" as string,
			linkMode: "create" as "create" | "edit",
			linkInfoHref: "" as string,
			pendingSelection: null as { from: number; to: number } | null,
			// used to trigger re-computation on selection changes
			selectionTick: 0 as number,
			eventUnsubs: [] as Array<() => void>,
			linkTimeout: null as ReturnType<typeof setTimeout> | null,
		};
	},
	computed: {
		isLinkActive(): boolean {
			// make this computed reactive to selection changes
			void this.selectionTick;
			const e = this.editor as any;
			if (!e) return false;
			let linkActive = !!e.isActive?.("link");
			if (!linkActive) {
				const sel = e.state?.selection as any;
				if (sel && sel.empty) {
					const { from } = sel;
					const markType = e.state.schema.marks.link;
					let range = getMarkRange(e.state.doc.resolve(from), markType) as any;
					if (!range && from > 1) {
						const prevPos = Math.max(1, from - 1);
						range = getMarkRange(e.state.doc.resolve(prevPos), markType) as any;
					}
					if (!range) {
						const docSize = e.state.doc.content.size ?? e.state.doc.nodeSize ?? 0;
						const nextPos = Math.min(Math.max(1, docSize), from + 1);
						if (nextPos !== from) {
							range = getMarkRange(e.state.doc.resolve(nextPos), markType) as any;
						}
					}
					if (range && (range.to === from || range.from === from)) {
						linkActive = true;
					}
				}
			}
			return linkActive;
		},
	},
	methods: {
		openLinkDialog() {
			const e = this.editor as any;
			if (!e) return;
			const { from, to } = e.state.selection;
			this.pendingSelection = { from, to };
			let selectedText = from !== to ? e.state.doc.textBetween(from, to, " ") : "";
			if (!selectedText && e.isActive("link")) {
				const $pos = e.state.doc.resolve(from);
				const markType = e.state.schema.marks.link;
				const range = getMarkRange($pos, markType) as any;
				if (range) {
					this.pendingSelection = { from: range.from, to: range.to };
					selectedText = e.state.doc.textBetween(range.from, range.to, " ");
				}
			}
			const prevHref = (e.getAttributes("link")?.href as string | undefined) || "";
			this.linkUrl = prevHref || "";
			this.linkTitle = selectedText || (prevHref ? stripProtocol(prevHref) : "");
			this.linkInfoHref = prevHref || "";
			this.linkMode = prevHref ? "edit" : "create";
			this.showLinkModal = true;
			// Focus the input after modal is visible
			setTimeout(() => {
				const input = this.$refs.linkUrlInput as HTMLInputElement | null;
				if (input && input.offsetParent !== null) {
					input.focus();
				}
			}, 120);
		},
		submitLink() {
			const e = this.editor as any;
			if (!e) return;
			const href = normalizeUrl(this.linkUrl || "");
			if (!href) {
				this.pendingSelection = null;
				this.showLinkModal = false;
				return;
			}
			let from: number;
			let to: number;
			if (this.pendingSelection) {
				from = this.pendingSelection.from;
				to = this.pendingSelection.to;
				e.chain().setTextSelection({ from, to }).run();
			} else {
				({ from, to } = e.state.selection as any);
			}
			let hasSelection = from !== to;
			if (!hasSelection && e.isActive("link")) {
				const $pos = e.state.doc.resolve(from);
				const markType = e.state.schema.marks.link;
				const range = getMarkRange($pos, markType) as any;
				if (range) {
					e.chain().setTextSelection(range).run();
					from = range.from;
					to = range.to;
					hasSelection = true;
				}
			}
			const selectedText = hasSelection ? (e.state.doc as any).textBetween(from, to, " ") : "";
			const tsAttrs = (e.getAttributes("textStyle") as Record<string, any>) || {};
			const activeTextColor = (tsAttrs.color as string) || undefined;
			const baseLinkAttrs: Record<string, any> = { href, class: "text-blue-400" };
			const linkAttrs = activeTextColor
				? { ...baseLinkAttrs, style: `color: ${activeTextColor}` }
				: baseLinkAttrs;
			if (hasSelection) {
				const desiredText = (this.linkTitle || selectedText).trim();
				if (desiredText && desiredText !== selectedText) {
					e.chain()
						.focus()
						.deleteSelection()
						.insertContent({ type: "text", text: desiredText, marks: [{ type: "link", attrs: linkAttrs }] })
						.run();
					moveCaretOutOfLink(e);
				} else {
					e.chain()
						.focus()
						.extendMarkRange("link")
						.setLink(linkAttrs as any)
						.run();
					moveCaretOutOfLink(e);
				}
			} else {
				const display = (this.linkTitle && this.linkTitle.trim()) || stripProtocol(href);
				e.chain()
					.focus()
					.insertContent({ type: "text", text: display, marks: [{ type: "link", attrs: linkAttrs }] })
					.run();
				moveCaretOutOfLink(e);
			}
			this.pendingSelection = null;
			this.showLinkModal = false;
			this.linkTimeout = setTimeout(() => {
				if (e && !e.isDestroyed) {
					e.commands.focus();
				}
			}, 0);
		},
		cancelLink() {
			this.pendingSelection = null;
			this.showLinkModal = false;
		},
		unsetLink() {
			const ed = this.editor as any;
			if (!ed) return;
			const { from, to } = ed.state.selection;
			if (from === to) {
				ed.chain().focus().extendMarkRange("link").unsetLink().run();
			} else {
				ed.chain().focus().unsetLink().run();
			}
			this.showLinkModal = false;
		},
		toggleBulletList() {
			(this.editor as any)?.chain?.().focus().toggleBulletList().run();
		},
		toggleOrderedList() {
			(this.editor as any)?.chain?.().focus().toggleOrderedList().run();
		},
		// Methods exposed for parent refs
		openFromSelection() {
			this.openLinkDialog();
		},
		isModalOpen() {
			return this.showLinkModal;
		},
		closeModal() {
			this.showLinkModal = false;
		},
	},
	mounted() {
		const e = this.editor as any;
		if (!e || typeof e.on !== "function") return;
		const bump = () => {
			this.selectionTick++;
		};
		e.on("selectionUpdate", bump);
		e.on("update", bump);
		e.on?.("transaction", bump);
		(this.eventUnsubs as any) = [
			() => e.off?.("selectionUpdate", bump),
			() => e.off?.("update", bump),
			() => e.off?.("transaction", bump),
		];
	},
	beforeUnmount() {
		clearTimeout(this.linkTimeout);
		for (const fn of this.eventUnsubs) {
			try {
				fn();
			} catch {}
		}
		this.eventUnsubs = [];
	},
});
</script>
